import { useContext, useEffect, useRef, useState } from "react";
import DeleteIcon from "components/SVGs/Delete";
import { IChangedTarget, RenderInput } from "../../common/Elements";
import { useTranslation } from "react-i18next";
import { IClassRecord } from "./AddingIndividually";
import { validateProperty } from "./utils/validate";
import { IValidationErrors } from "features/common/Interfaces";
import SelectInput from "components/SelectInput";
import { MasterDataContext } from "contexts/MasterDataContext";
import { gradesList, teachersList } from "services/Common";
import { Typography } from "@mui/material";

const customStyles = {
  form: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    flexDirection: "row",
    width: "100%",
    "&:hover": {
      background: "rgba(181,181,181,0.2)",
      borderRadius: "5px",
    },
  },
};
export interface IClassRecordInput {
  currentTarget: { name: "className" | "gradeId" | "teacherId"; value: string };
}
type Props = {
  index: number;
  data: IClassRecord;
  validationErrors: IValidationErrors;
  getRecord(data: IClassRecord, index: number): void;
  removeRecord(index: number): void;
  noLabel?: boolean;
};

const ClassRecord = ({
  index,
  data,
  validationErrors,
  getRecord,
  removeRecord,
  noLabel = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState<IClassRecord>(data);
  const [grades, setGrades] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const [recordIndex] = useState(index);
  const [errors, setErrors] = useState<IValidationErrors>(validationErrors);
  const nameRef = useRef<HTMLInputElement>(null);
  const fetchGrades = async () => {
    try {
      const response = await gradesList();
      if (response?._statusCode === 200) {
        const list = response._entity?.map((item: any) => ({
          value: item._id,
          label: t(item._code),
        }));
        setGrades(list);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTeachers = async (schoolId: number) => {
    try {
      const response = await teachersList(schoolId);
      if (response?._statusCode === 200) {
        const list = response._entity?.map((item: any) => ({
          value: item._teacherId,
          label: (
            <span
              style={{
                display: "block",
                whiteSpace: "nowrap",
              }}
            >
              {`${item?._name ?? ""} ${item?._lastName ?? ""}`}
            </span>
          ),
        }));
        setTeachers(list);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGrades();
    if (schoolDetails?.schoolId) fetchTeachers(schoolDetails?.schoolId);
  }, [schoolDetails?.schoolId]);

  const gradeChangeHandler = (event: any) => {
    setFormData({
      ...formData,
      gradeId: event.currentTarget.value,
    });
  };
  const teacherChangeHandler = (event: any) => {
    setFormData({
      ...formData,
      teacherId: event.currentTarget.value,
    });
  };
  const formVars = {
    className: {
      name: "className",
      label: !noLabel ? t("Class Name") : "",
      placeholder: t("Class Name"),
      value: formData?.className,
      autoFocus: true,
    },
    grade: {
      name: "gradeId",
      label: !noLabel ? t("Select Grade") : "",
      defaultValue: formData?.gradeId,
      valueList: grades,
      getOption: gradeChangeHandler,
    },
    teacher: {
      name: "teacherId",
      label: !noLabel ? t("Select Teacher") : "",
      defaultValue: formData?.teacherId,
      valueList: teachers,
      getOption: teacherChangeHandler,
    },
  };

  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);
  useEffect(() => {
    setErrors(validationErrors);
  }, [JSON.stringify(validationErrors)]);

  const changeHandler = (event: IChangedTarget) => {
    let data = { ...formData };
    const { currentTarget: input } = event as IClassRecordInput;
    const errorsObject: any = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) errorsObject[input.name] = errorMessage;
    else delete errorsObject[input.name];
    setErrors(errorsObject);
    data = { ...data, [input.name]: input.value };
    setFormData(data);
  };
  const submitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    const updateFormData = {
      ...formData,
      className: formData.className?.trim(),
    };
    getRecord(updateFormData, index);
  };

  return (
    <Typography
      component="form"
      sx={{ ...customStyles.form, direction: i18n.dir(), zIndex: 60 - index }}
      key={"record" + recordIndex}
      onSubmit={submitHandler}
    >
      <SelectInput
        {...formVars.grade}
        placeholder={t("Select")}
        style={{ container: { zIndex: 60 - index } }}
        dropDownHeight="125px"
        onBlur={submitHandler}
      />
      <SelectInput
        {...formVars.teacher}
        placeholder={t("Select")}
        style={{ container: { zIndex: 60 - index } }}
        onBlur={submitHandler}
        dropDownHeight="100px"
        searchable
      />
      {RenderInput({
        ...formVars.className,
        errors: t(errors.className),
        changeHandler,
        styles: { padding: "0 3px", width: "100%" },
        onBlur: submitHandler,
        tabIndex: index + 2,
      })}
      <DeleteIcon
        sx={{
          background: "rgb(105 123 140)",
          borderRadius: "50%",
          padding: "3px",
          margin: "5px 0",
          cursor: "pointer",
          visibility:
            formData.className.length || formData.teacherId || formData.gradeId
              ? "visible"
              : "hidden",
          "&> path": { fill: "rgb(255 255 255)" },
        }}
        onClick={() => removeRecord(recordIndex)}
      />
      <button type="submit" style={{ visibility: "hidden" }} />
    </Typography>
  );
};

export default ClassRecord;
